/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-nested-ternary */

'use client';

import * as React from 'react';
import { format, isSameDay, startOfDay, subDays, subMonths } from 'date-fns';
import { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { CLEAN_FILTERS } from '@/components/conversation-insights/filters-bar';
import DateRangeIcon from '../../public/icons/new-dashboard/calendar-02.svg';

export const toUTCMidnight = (d: Date): Date => {
  const localMid = startOfDay(d);  
  return new Date(
    Date.UTC(
      localMid.getFullYear(),
      localMid.getMonth(),
      localMid.getDate(),
    )
  );
};

/** remove div’s native `onChange` so we can redefine it */
interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  filters: any;
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
}

export function DateRangePicker({ className, filters, value, onChange }: Props) {
  const [open, setOpen] = React.useState(false)
  const today = new Date();
  const sixMonthsAgo = subMonths(today, 6);
  const thirtyAgo = subDays(today, 30);

  const [date, setDate] = React.useState<DateRange | undefined>({
    from: toUTCMidnight(value?.from),
    to: toUTCMidnight(value?.to),
  })

  React.useEffect(()=>{
    if(date){
      onChange({
        from: toUTCMidnight(date.from),
        to:   toUTCMidnight(date.to),
      })
    }
  },[date])

  React.useEffect(()=>{
    if(!open && (!(date?.from && date?.to) || isSameDay(date.from, date.to))){
      setDate({
        from: toUTCMidnight(thirtyAgo),
        to: toUTCMidnight(today),
      })
    }
  },[open])

  React.useEffect(()=>{
    if(filters.search_date === CLEAN_FILTERS.search_date){
      setDate({
        from: thirtyAgo,
        to: today,
      })
    } 
  },[filters])

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'w-[245px] justify-start text-left font-normal gap-2',
              !value && 'text-muted-foreground',
            )}
          >
            <DateRangeIcon className="size-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="range"
            numberOfMonths={2}
            defaultMonth={date?.from}
            selected={date}
            onSelect={setDate}
            initialFocus
            disabled={{ before: sixMonthsAgo, after: today }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
